"use client"

import { FileSearch } from "lucide-react"

import { GlassCard } from "@shared/ui/landing/glass-card"
import { LiveBadge } from "@shared/ui/landing/status-tag"
import { SectionContainer } from "@shared/ui/landing/section-container"
import { StatCounter } from "@shared/ui/landing/stat-counter"
import { buttonVariants } from "@shared/ui/components/button"
import { cn } from "@shared/ui/lib/utils"

import { useTranslations } from "@/lib/i18n/client"
import { HeroGlobe } from "@/components/hero-globe"
import { StartScanButton } from "@/components/start-scan-button"
import { getLiveStatFluctuation } from "@/lib/live-data"

export function HeroSection() {
  const t = useTranslations("hero")
  const tStats = useTranslations("stats")

  const stats = [
    {
      value: getLiveStatFluctuation(18, 0.008) / 10,
      suffix: "B",
      decimals: 1,
      label: tStats("addresses"),
    },
    {
      value: getLiveStatFluctuation(12, 0.01),
      suffix: "M",
      decimals: 0,
      label: tStats("entities"),
    },
    {
      value: getLiveStatFluctuation(190, 0.005),
      suffix: "+",
      decimals: 0,
      label: tStats("jurisdictions"),
    },
  ] as const

  return (
    <SectionContainer className="bts-section-glow pb-8 pt-10 md:pt-16">
      <div className="grid items-center gap-10 lg:grid-cols-2">
        <div className="relative z-10 space-y-6">
          <LiveBadge label={t("liveLabel")} />
          <h1 className="text-4xl font-bold tracking-tight text-foreground drop-shadow-sm md:text-5xl lg:text-6xl">
            {t("title")}
          </h1>
          <p className="max-w-xl text-base text-muted-foreground md:text-lg">
            {t("subtitle")}
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <StartScanButton />
            <a
              href="#report"
              className={cn(
                buttonVariants({ variant: "outline", size: "lg" }),
                "gap-2 border-white/15 bg-white/5"
              )}
            >
              <FileSearch className="size-4" />
              {t("viewSample")}
            </a>
          </div>
          <div className="grid grid-cols-3 gap-3 pt-4">
            {stats.map((stat) => (
              <GlassCard key={stat.label} className="p-4 text-center">
                <p className="text-xl font-semibold tabular-nums md:text-2xl">
                  <StatCounter
                    value={stat.value}
                    suffix={stat.suffix}
                    decimals={stat.decimals}
                  />
                </p>
                <p className="mt-1 text-xs text-muted-foreground">{stat.label}</p>
              </GlassCard>
            ))}
          </div>
        </div>
        <HeroGlobe />
      </div>
    </SectionContainer>
  )
}
